import React, { useState, useEffect } from 'react';
import { useExtractionContext } from '../../context/ExtractionContext';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, SelectProps, TextField, Checkbox, Radio, RadioGroup, FormControlLabel, Box, Chip, CircularProgress, IconButton, AutocompleteRenderInputParams, Autocomplete, Button, Typography, Paper } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CancelIcon from "@mui/icons-material/Cancel";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { FieldAttributes, Form, Formik, useField } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { StyledDropzone } from '../Elements/Dropzone';
import * as yup from 'yup';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { PartnerModel } from '../../Pages/RequestSignature';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

const MULTIPLE_TAG_EXTRACT_ACCEPT = { 'application/pdf': ['.pdf'] as const } as const;
/** Native `<input accept>` value so the OS file dialog filters to PDFs (MIME + extension). */
const MULTIPLE_TAG_INPUT_ACCEPT = 'application/pdf,.pdf';

function isPdfFile(file: File): boolean {
    return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
};

export interface AddReportTagModel {
    partnersId: number;
    stateId: number;
    isMultipleTags?: boolean;
    numberOfTags: string;
    invoiceNumber: string;
    insuredName: string;
    policyNumber: string;
    tagNumber: string;
    errorTagCodes: number[];
    files: File[];
    filerNotes: string;
    /** Present only for multiple-tag extraction rows; omitted for single-tag flow. */
    dueDate?: string;
};

interface Iprops {
    handleClose: () => void;
    Open: boolean;
    partners: { partnersId: number, customerName: string }[];
    states: { stateId: number, stateName: string }[];
    errorTagCodes: { errorTagCodeId: number, errorTagName: string }[];
};

/** Maps extraction due date (typically MM-DD-YYYY) to YYYY-MM-DD for `<input type="date" />`. */
function extractionDueDateToInputValue(raw: unknown): string {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (!s) return '';
    const isoCandidate = s.split('T')[0].slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) return isoCandidate;
    const us = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
    if (us) {
        const month = Number(us[1]);
        const day = Number(us[2]);
        const year = Number(us[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
    }
    return '';
};

const AddReportTagValidationSchema = yup.object({
    partnersId: yup.number()
        .transform((v) => (v === '' || v === undefined || v === null ? undefined : Number(v)))
        .required('Client is required')
        .min(1, 'Please select a Client'),
    stateId: yup.number()
        .transform((v) => (v === '' || v === undefined || v === null ? undefined : Number(v)))
        .required('State is required')
        .min(1, 'Please select a State'),
    numberOfTags: yup.string()
        .required('Number of Tags is required')
        .oneOf(['single', 'multiple'], 'Please select Single or Multiple'),
    invoiceNumber: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Invoice Number is required for single tags'),
        otherwise: (schema) => schema
    }),
    insuredName: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Insured Name is required for single tags'),
        otherwise: (schema) => schema
    }),
    policyNumber: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Policy Number is required for single tags'),
        otherwise: (schema) => schema
    }),
    tagNumber: yup.string()
        .optional()
        .matches(/^\d*$/, 'Tag Number must be numeric'),
    errorTagCodes: yup.array()
        .of(yup.number())
        .when('numberOfTags', {
            is: 'single',
            then: (schema) => schema.min(1, 'At least one Error Type Code must be selected').required('At least one Error Type Code must be selected'),
            otherwise: (schema) => schema.optional().default([]),
        }),
    // file: yup.mixed<File>()
    //     .required('A file must be selected')
    //     .test('file-required', 'A file must be selected', (value) => value !== null && value !== undefined),
    filerNotes: yup.string()
});

export const AddReportTag: React.FC<Iprops> = ({ handleClose, Open, partners, states, errorTagCodes }) => {
    const { user } = useAuth();
    const token = user?.token;
    const GreenBtnColor = '#4caf50';
    const OrangeBtnColor = '#ff7b00';
    const PrimaryBlueColor = '#0d4a63';
    const [loading, setLoading] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const {
        extractedValues,
        setExtractedValues,
        extractionInProgress,
        extractionTimedOut,
        setExtractionInProgress,
        onExtractionSuccess: notifyExtractionSuccess,
        onExtractionError,
        onExtractionTimeout,
        clearExtraction,
    } = useExtractionContext();
    // Lazy initializer: if context already has extracted values (e.g. the user navigated
    // away mid-extraction and is now returning), restore enough form state so that the
    // "multiple tags" section renders and the results are immediately visible.
    const [formData, setFormData] = useState(() => {
        const hasResults = extractedValues.length > 0;
        return {
            partnersId: hasResults ? extractedValues[0].partnersId : 0,
            stateId: hasResults ? extractedValues[0].stateId : 0,
            numberOfTags: hasResults ? 'multiple' : '',
            invoiceNumber: '',
            insuredName: '',
            policyNumber: '',
            tagNumber: '',
            errorTagCodes: [] as number[],
            files: hasResults ? (extractedValues[0]?.files ?? [] as File[]) : [] as File[],
            filerNotes: '',
        };
    });
    // Tracks how many files in values.files were pre-populated from extraction (non-removable).
    const [lockedFileCount, setLockedFileCount] = useState(() => {
        const hasResults = extractedValues.length > 0;
        return hasResults ? (extractedValues[0]?.files?.length ?? 0) : 0;
    });

    // The Dialog has keepMounted, so this component is always mounted and the lazy
    // initializer above runs before extractedValues is populated (e.g. when loaded
    // from an email link). Sync the three header fields whenever extracted values arrive
    // and the form hasn't been filled in yet.
    useEffect(() => {
        if(extractedValues.length > 0 && !formData.numberOfTags){
            const extractionFiles = extractedValues[0]?.files ?? [];
            setFormData(prev => ({
                ...prev,
                partnersId: extractedValues[0].partnersId,
                stateId: extractedValues[0].stateId,
                numberOfTags: 'multiple',
                files: extractionFiles,
            }));
            setLockedFileCount(extractionFiles.length);
        }
    }, [extractedValues, formData.numberOfTags]);
    
    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                {/* <FormHelperText className='formtexthelper-heading'>Client</FormHelperText> */}
                <Autocomplete
                    options={partners}
                    getOptionLabel={(option: PartnerModel) => option.customerName}
                    value={partners.find((item) => Number(item.partnersId) === Number(field.value)) ?? null}
                    sx={{
                        '& .MuiFormLabel-root': { transform: 'translate(1px, -28px) scale(1)', fontSize: '16px !important', color: '#212529 !important', fontWeight: '400 !important' },
                        '& .MuiInputBase-root': { padding: '10.5px 9px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px', border: '2px solid #e2e8f0', padding: '12px 16px', top: '-1px'},
                        paddingTop: '30px'
                    }}
                    onChange={(e, value) => {
                    const partnersId = value?.partnersId != null ? Number(value.partnersId) : 0;
                    helpers.setValue(partnersId);
                    }}
                    onBlur={() => helpers.setTouched(true)}
                    renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} sx={{ '& .MuiInputBase-input': { height: '30px !important' }, '& .MuiOutlinedInput-notchedOutline legend' : { display: 'none'} }} label="Select Client" />}
                />
                {/* <Select {...field}
                    labelId="Partner-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}>
                    {partners.map((partner, key) =>
                        <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                    )}
                </Select> */}
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectStates: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>State</FormHelperText>
                <Select {...field}
                    labelId="State-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    sx={{
                        '& .MuiSelect-select': { padding: '16px' },
                        '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px', paddingTop: '18px', paddingBottom: '18px', top: '-7px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                    }}
                >
                    {states.map((state, key) => (
                        <MenuItem key={key} value={state.stateId}>{state.stateName}</MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };

    const onCloseModal = () => {
        if (extractionInProgress) {
            // Extraction is in progress — silently hide; context preserves state across navigation
            handleClose();
            return;
        }
        setExtractedValues([]);
        setFormData({
            partnersId: 0,
            stateId: 0,
            numberOfTags: '',
            invoiceNumber: '',
            insuredName: '',
            policyNumber: '',
            tagNumber: '',
            errorTagCodes: [],
            files: [],
            filerNotes: '',
        });
        setFormKey((k) => k + 1);
        clearExtraction();
        handleClose();
    };

    return (
        <Dialog
            open={Open}
            onClose={onCloseModal}
            keepMounted
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 1200,
                    minHeight: '70vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                    padding: '0 15px 15px !important'
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: '-15px',
                    marginRight: '-15px',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, }}>
                    <LocalOfferOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        Create New Tag
                    </Typography>
                </Box>
                <IconButton
                    onClick={onCloseModal}
                    sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <Formik 
                key={formKey}
                initialValues={formData}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validateOnMount={true}
                validationSchema={AddReportTagValidationSchema}
                onSubmit={(data, { setSubmitting, setErrors }) => {
                    const clientName = partners.find((partner) => partner.partnersId === data.partnersId)?.customerName || 'the selected partner';
                    if (data.numberOfTags === 'multiple' && (!extractedValues || extractedValues.length === 0)) {
                        Swal.fire({
                            title: 'No tags to send',
                            text: 'Please upload a document and extract tags first, or add at least one tag manually.',
                            icon: 'warning',
                        });
                        return;
                    }
                    if (data.numberOfTags === 'multiple' && extractedValues.some((tag) => (tag.errorTagCodes?.length ?? 0) < 1)) {
                        Swal.fire({
                            title: 'Error type codes required',
                            text: 'Each tag must have at least one error type code before sending to the client.',
                            icon: 'warning',
                        });
                        return;
                    }
                    if (data.numberOfTags === 'multiple' && extractedValues.some((tag) => !String(tag.dueDate ?? '').trim())) {
                        Swal.fire({
                            title: 'Due date required',
                            text: 'Each extracted tag must have a due date before sending to the client.',
                            icon: 'warning',
                        });
                        return;
                    }
                    
                    Swal.fire({
                        title: `<div style="color: white;">Are you sure?</div>`,
                        html: `<div style="color: white;">This will send a tag to the Client Portal for <span style="color: yellow;">${clientName}</span></div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, send it!',
                        cancelButtonText: 'No, Continue to EDIT',
                        confirmButtonColor: GreenBtnColor,
                        cancelButtonColor: OrangeBtnColor,
                        reverseButtons: true,
                        background: PrimaryBlueColor,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSubmitting(true);
                            setLoading(true);

                            // Build list of payloads: one for single, one per tag for multiple (same shape as single)
                            const tagsToSend = data.numberOfTags === 'single'
                                ? [{
                                    invoiceNumber: data.invoiceNumber || '',
                                    insuredName: data.insuredName || '',
                                    policyNumber: data.policyNumber || '',
                                    tagNumber: data.tagNumber || '',
                                    errorTagCodes: data.errorTagCodes || [],
                                    filerNotes: data.filerNotes || '',
                                }]
                                : extractedValues.map((tag) => {
                                    const str = (v: unknown) => (v == null || v === undefined ? '' : String(v)).trim();
                                    return {
                                        // Per-tag values; fall back to shared form fields when extraction left blanks (e.g. misc / missing extract fields)
                                        invoiceNumber: str(tag.invoiceNumber) || str(data.invoiceNumber),
                                        insuredName: str(tag.insuredName) || str(data.insuredName),
                                        policyNumber: str(tag.policyNumber) || str(data.policyNumber),
                                        tagNumber: tag.tagNumber || '',
                                        errorTagCodes: tag.errorTagCodes || [],
                                        filerNotes: tag.filerNotes || '',
                                        dueDate: str(tag.dueDate),
                                    };
                                });

                            const sendOne = (payload: Record<string, unknown>, files: File[]) => {
                                const formData = new FormData();
                                // Multiple tags from one document: N separate API calls, each as a single tag (not IsMultipleTags on the server)
                                const numberOfTagsForApi = data.numberOfTags === 'multiple' ? 'single' : data.numberOfTags;
                                const dataPayload = {
                                    partnersId: data.partnersId,
                                    stateId: data.stateId,
                                    numberOfTags: numberOfTagsForApi,
                                    ...payload,
                                };
                                formData.append("data", JSON.stringify(dataPayload));
                                if (files && files.length > 0) {
                                    files.forEach((file) => formData.append("files", file));
                                }
                                return apiClient('/ReportTags/addReportTag', {
                                    method: 'POST',
                                    headers: new Headers({
                                        'Authorization': `Bearer ${token}`,
                                    }),
                                    body: formData,
                                });
                            };

                            const runRequests = async () => {
                                const files = (data.files && data.files.length > 0) ? data.files : [];
                                for (let i = 0; i < tagsToSend.length; i++) {
                                    const response = await sendOne(tagsToSend[i], files);
                                    if (!response || !response.status) {
                                        throw new Error(response?.message || 'Failed to send tag');
                                    }
                                }
                            };

                            runRequests().then(() => {
                                Swal.fire({
                                    title: 'Tag sent to client successfully',
                                    text: tagsToSend.length > 1
                                        ? `${tagsToSend.length} tags have been sent to the client.`
                                        : 'The tag has been sent to the client.',
                                    icon: 'success',
                                });
                                clearExtraction();
                                setLockedFileCount(0);
                                setFormData({
                                    partnersId: 0,
                                    stateId: 0,
                                    numberOfTags: '',
                                    invoiceNumber: '',
                                    insuredName: '',
                                    policyNumber: '',
                                    tagNumber: '',
                                    errorTagCodes: [],
                                    files: [],
                                    filerNotes: '',
                                });
                                setFormKey((k) => k + 1);
                                handleClose();
                            }).catch((error) => {
                                console.error('Error sending tag to client:', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: error.message || 'Failed to send tag to client.',
                                    icon: 'error',
                                });
                            }).finally(() => {
                                setSubmitting(false);
                                setLoading(false);
                            });
                        } else {
                            setSubmitting(false);
                            setLoading(false);
                        }
                    });
                }}
            >
                {({ values, isSubmitting, setFieldValue, setFieldTouched, errors, touched, isValid }) => {
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { name, value } = e.target;
                        setFieldValue(name, value);
                        setFieldTouched(name, true);
                    };
                    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        setFieldValue("numberOfTags", value);
                        // Don't mark as touched immediately - only on blur
                        
                        // reset extracted values when number of tags is changed to single
                        if (value === 'single') {
                            setExtractedValues([]);
                            setFieldValue("files", [] as File[]);
                            setLockedFileCount(0);
                        }else{
                            setFieldValue("tagNumber", '');
                            setFieldValue("errorTagCodes", []);
                            setFieldValue("filerNotes", '');
                            setFieldValue("files", [] as File[]);
                            setLockedFileCount(0);
                        }
                    };
                    const updateExtractedValue = (index: number, field: keyof AddReportTagModel, value: string | number[]) => {
                        setExtractedValues((prev) =>
                            prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
                        );
                    };
                    const errorTagCodesSorted = [...errorTagCodes].sort((a, b) => a.errorTagCodeId - b.errorTagCodeId);
                    const errorTagMid = Math.ceil(errorTagCodesSorted.length / 2);
                    const errorTagCodesColumn1 = errorTagCodesSorted.slice(0, errorTagMid);
                    const errorTagCodesColumn2 = errorTagCodesSorted.slice(errorTagMid);
                    const removeExtractedTag = (index: number) => {
                        setExtractedValues((prev) => prev.filter((_, i) => i !== index));
                    };
                    /** Extract data now button handler */
                    const handleExtractDataNow = () => {
                        if (!values.files?.length) {
                            setExtractedValues([]);
                            Swal.fire({
                                title: 'Files required',
                                text: 'Please upload at least one file before extracting data.',
                                icon: 'error',
                            });
                            return;
                        }
                        setExtractionInProgress(true);

                        const formData = new FormData();
                        formData.append('data', JSON.stringify({
                            partnersId: values.partnersId,
                            stateId: values.stateId,
                        }));
                        formData.append('file', values.files[0]);

                        apiClient('/ReportTags/extractTagReport', {
                            method: 'POST',
                            headers: new Headers({
                                'Authorization': `Bearer ${token}`,
                            }),
                            body: formData,
                        }).then((responseJson) => {
                            if(responseJson.timedOut){
                                onExtractionTimeout(responseJson.extractionId);
                                return;
                            }
                            if(!responseJson.status){
                                Swal.fire({
                                    title: 'Error',
                                    text: responseJson.message || 'Failed to extract tag report.',
                                    icon: 'error',
                                });
                                onExtractionError();
                                return;
                            }
                            const extractedRecords = responseJson.data;
                            const mappedValues = extractedRecords.map((record: any) => {
                                const dueDateStr = extractionDueDateToInputValue(record?.dueDate);
                                return {
                                    partnersId: values.partnersId,
                                    stateId: values.stateId,
                                    numberOfTags: 'single',
                                    invoiceNumber: record?.invoiceNumber ?? '',
                                    insuredName: record?.insuredName ?? '',
                                    policyNumber: record?.policyNumber ?? '',
                                    tagNumber: Number(record?.tagNumber),
                                    errorTagCodes: record?.errorTagCodeIds,
                                    filerNotes: record?.filerNotes ?? '',
                                    dueDate: dueDateStr,
                                    files: [],
                                };
                            });
                            notifyExtractionSuccess(mappedValues);
                        }).catch((error) => {
                            console.error('Error extracting data:', error);
                            onExtractionError();
                        });
                    };
                    return (
                        <Form className='main-form'>
                            <DialogContent sx={{ width: '100%', minHeight: '53.5vh', overflow: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                                <Grid container spacing={3}>
                                    {/* Section: Client & State */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Client & State
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <FMSelectPartnersCustom name="partnersId" />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FMSelectStates name="stateId" />
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    {(values.partnersId > 0 && values.stateId > 0) && (
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Number of Tags
                                        </Typography>
                                        <FormControl sx={{ width: '100%' }} error={touched.numberOfTags && !!errors.numberOfTags}>
                                            <RadioGroup
                                                row
                                                name="numberOfTags"
                                                value={values.numberOfTags}
                                                onChange={handleRadioChange}
                                                onBlur={() => setFieldTouched("numberOfTags", true)}
                                                sx={{ gap: 2 }}
                                            >
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 140,
                                                        p: 2,
                                                        cursor: 'pointer',
                                                        border: 2,
                                                        borderColor: values.numberOfTags === 'single' ? 'primary.main' : 'divider',
                                                        backgroundColor: values.numberOfTags === 'single' ? 'primary.main' : 'background.paper',
                                                        color: values.numberOfTags === 'single' ? 'primary.contrastText' : 'text.primary',
                                                        borderRadius: 2,
                                                        transition: 'border-color 0.2s, background-color 0.2s',
                                                        '&:hover': { borderColor: 'primary.light', backgroundColor: values.numberOfTags === 'single' ? 'primary.dark' : 'action.hover' },
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="single"
                                                        control={<Radio sx={{ color: values.numberOfTags === 'single' ? 'primary.contrastText' : 'primary.main','&.Mui-checked': {
                                                            color: '#FFFFFF',
                                                        }, }} />}
                                                        label={<Typography fontWeight={500}>Single tag</Typography>}
                                                        sx={{ m: 0, width: '100%' }}
                                                    />
                                                    <Typography variant="body2" sx={{ opacity: 0.9, pl: 4, mt: -0.5 }}>One policy, one tag</Typography>
                                                </Paper>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 140,
                                                        p: 2,
                                                        cursor: 'pointer',
                                                        border: 2,
                                                        borderColor: values.numberOfTags === 'multiple' ? 'primary.main' : 'divider',
                                                        backgroundColor: values.numberOfTags === 'multiple' ? 'primary.main' : 'background.paper',
                                                        color: values.numberOfTags === 'multiple' ? 'primary.contrastText' : 'text.primary',
                                                        borderRadius: 2,
                                                        transition: 'border-color 0.2s, background-color 0.2s',
                                                        '&:hover': { borderColor: 'primary.light', backgroundColor: values.numberOfTags === 'multiple' ? 'primary.dark' : 'action.hover' },
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="multiple"
                                                        control={<Radio sx={{ color: values.numberOfTags === 'multiple' ? 'primary.contrastText' : 'primary.main', '&.Mui-checked': {
                                                            color: '#FFFFFF',
                                                        }, }} />}
                                                        label={<Typography fontWeight={500}>Multiple tags</Typography>}
                                                        sx={{ m: 0, width: '100%' }}
                                                    />
                                                    <Typography variant="body2" sx={{ opacity: 0.9, pl: 4, mt: -0.5 }}>Upload a document to extract data automatically.</Typography>
                                                </Paper>
                                            </RadioGroup>
                                            {touched.numberOfTags && errors.numberOfTags && (
                                                <FormHelperText error sx={{ mt: 1 }}>{errors.numberOfTags}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    )}
                                    {values.numberOfTags === 'single' ? (<>
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Tag details
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="invoiceNumber" label="Invoice Number" 
                                                        value={values.numberOfTags === 'single' ? values.invoiceNumber : ''}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("invoiceNumber", true)}
                                                        error={touched.invoiceNumber && !!errors.invoiceNumber}
                                                        helperText={touched.invoiceNumber && errors.invoiceNumber ? String(errors.invoiceNumber) : 'Enter N/A if no invoice number exists'}
                                                        fullWidth 
                                                        sx={{
                                                            '& .MuiInputBase-input': { height: '52px !important' },
                                                            '& .MuiOutlinedInput-notchedOutline' : { border: '2px solid #e2e8f0' }
                                                        }}
                                                        // disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="insuredName" label="Insured Name" 
                                                        value={values.numberOfTags === 'single' ? values.insuredName : ''}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("insuredName", true)}
                                                        error={touched.insuredName && !!errors.insuredName}
                                                        helperText={touched.insuredName && errors.insuredName}
                                                        fullWidth 
                                                        sx={{
                                                            '& .MuiInputBase-input': { height: '52px !important' },
                                                            '& .MuiOutlinedInput-notchedOutline' : { border: '2px solid #e2e8f0' }
                                                        }}
                                                        // disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="policyNumber" label="Policy Number" 
                                                        value={values.numberOfTags === 'single' ? values.policyNumber : ''}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("policyNumber", true)}
                                                        error={touched.policyNumber && !!errors.policyNumber}
                                                        helperText={touched.policyNumber && errors.policyNumber}
                                                        fullWidth 
                                                        sx={{
                                                            '& .MuiInputBase-input': { height: '52px !important' },
                                                            '& .MuiOutlinedInput-notchedOutline' : { border: '2px solid #e2e8f0' }
                                                        }}
                                                        // disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="tagNumber" label="Tag Number" 
                                                        value={values.tagNumber}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("tagNumber", true)}
                                                        error={values.tagNumber?.length >= 100}
                                                        helperText={values.tagNumber?.length >= 100 ? 'Limited to 100 characters' : ''}
                                                        fullWidth 
                                                        sx={{
                                                            '& .MuiInputBase-input': { height: '52px !important' },
                                                            '& .MuiOutlinedInput-notchedOutline' : { border: '2px solid #e2e8f0' }
                                                        }}
                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 100 }}
                                                        // disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                                <FormControl sx={{ width: '100%' }} error={touched.errorTagCodes && !!errors.errorTagCodes}>
                                                    <FormHelperText className='formtexthelper-heading' sx={{ fontWeight: 600, mb: 1 }}>
                                                        Error type code
                                                    </FormHelperText>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select at least one error type for this tag.</Typography>
                                                    <Grid container spacing={1} columnSpacing={2}>
                                                        {[errorTagCodesColumn1, errorTagCodesColumn2].map((columnCodes, colIndex) => (
                                                            <Grid item xs={12} sm={6} key={colIndex}>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    {columnCodes.map((errorTagCode: { errorTagCodeId: number, errorTagName: string }) => (
                                                                        <FormControlLabel
                                                                            key={errorTagCode.errorTagCodeId}
                                                                            control={
                                                                                <Checkbox name="errorTagCodeId"
                                                                                    checked={(values.errorTagCodes as number[]).includes(errorTagCode.errorTagCodeId)}
                                                                                    value={errorTagCode.errorTagCodeId}
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setFieldValue("errorTagCodes", [...values.errorTagCodes, Number(e.target.value)]);
                                                                                        } else {
                                                                                            setFieldValue("errorTagCodes", (values.errorTagCodes as number[]).filter((id: number) =>
                                                                                                id !== Number(e.target.value)
                                                                                            ));
                                                                                        }
                                                                                        setFieldTouched("errorTagCodes", true);
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={`${errorTagCode.errorTagCodeId}: ${errorTagCode.errorTagName}`}
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                marginLeft: 0,
                                                                                marginRight: 0,
                                                                                alignItems: 'flex-start',
                                                                                width: '100%',
                                                                                '& .MuiFormControlLabel-label': {
                                                                                    cursor: 'pointer',
                                                                                    whiteSpace: 'normal',
                                                                                    lineHeight: 1.35,
                                                                                    paddingTop: 1.3,
                                                                                }
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                    {touched.errorTagCodes && errors.errorTagCodes && (
                                                        <FormHelperText error sx={{ mt: 0.5 }}>{errors.errorTagCodes}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    </>) : (<></>)}
                                    <Grid item xs={12}>
                                        {values.numberOfTags !== '' && (<>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            {values.numberOfTags === 'single' ? 'Attachments' : 'Tag report document'}
                                        </Typography>
                                        <FormControl sx={{ width: '100%' }} 
                                            // error={touched.file && !!errors.file}
                                        >
                                            <FormHelperText className='formtexthelper-heading' sx={{ mb: 0.5 }}>
                                                {values.numberOfTags === 'single' 
                                                    ? 'Select file(s) or drag and drop' 
                                                    : (!(values.stateId > 0) 
                                                        ? 'Upload the tag report document' 
                                                        : `Upload the tag report document for ${states.find((state) => state.stateId === values.stateId)?.stateName}`
                                                    )
                                                }
                                            </FormHelperText>
                                            {!(values.numberOfTags === 'multiple' && lockedFileCount > 0) && (
                                            <StyledDropzone
                                                label={values.numberOfTags === 'multiple' ? 'Select one document or Drag and Drop' : 'Select File(s) or Drag and Drop'}
                                                accept={values.numberOfTags === 'multiple' ? MULTIPLE_TAG_EXTRACT_ACCEPT : undefined}
                                                inputAccept={values.numberOfTags === 'multiple' ? MULTIPLE_TAG_INPUT_ACCEPT : undefined}
                                                useFsAccessApi={values.numberOfTags !== 'multiple'}
                                                onFileDrop={(files: File[]) => {
                                                    if (values.numberOfTags === 'multiple') {
                                                        if (files.length > 1) {
                                                            Swal.fire({
                                                                title: 'One document only',
                                                                text: 'You can only upload one document at a time when extracting data for multiple tags.',
                                                                icon: 'warning',
                                                            });
                                                        }
                                                        const first = files[0];
                                                        if (first && !isPdfFile(first)) {
                                                            Swal.fire({
                                                                title: 'PDF only',
                                                                text: 'When extracting multiple tags, please upload a PDF document.',
                                                                icon: 'warning',
                                                            });
                                                            return;
                                                        }
                                                        setFieldValue("files", files.length > 0 ? [files[0]] : []);
                                                    } else {
                                                        const existingFiles = values.files || [];
                                                        setFieldValue("files", [...existingFiles, ...files]);
                                                    }
                                                }}
                                            />
                                            )}
                                            {/* Display selected files */}
                                            {values.files && values.files.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <FormHelperText sx={{ mb: 1 }}>
                                                        {values.numberOfTags === 'multiple'
                                                            ? 'Selected document:'
                                                            : `Selected Files (${values.files.length}):`}
                                                    </FormHelperText>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {values.files.map((file, index) => {
                                                            const isLocked = index < lockedFileCount;
                                                            return (
                                                            <Chip
                                                                key={index}
                                                                label={`${file.name}`}
                                                                onDelete={isLocked ? undefined : () => {
                                                                    const updatedFiles = values.files.filter((_, i) => i !== index);
                                                                    setFieldValue("files", updatedFiles);
                                                                }}
                                                                deleteIcon={isLocked ? undefined : <CancelIcon />}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{
                                                                    maxWidth: '100%',
                                                                    '& .MuiChip-label': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }
                                                                }}
                                                            />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            )}
                                            {/* {touched.file && errors.file && (
                                                <FormHelperText error>{errors.file}</FormHelperText>
                                            )} */}
                                            {values.numberOfTags === 'multiple' && (
                                                <Typography variant="body2" sx={{ display: 'block', mt: 1.5, fontWeight: 600, }}>
                                                    PDF only. Maximum file size: 5 MB. Larger files may take a minute or more to process.
                                                </Typography>
                                            )}
                                        </FormControl>
                                        </>)}
                                    </Grid>
                                    {values.numberOfTags === 'single' && (<>
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Filer notes (optional)
                                        </Typography>
                                        <FormControl sx={{ width: '100%' }}>
                                            <TextField name="filerNotes" label="Add any notes for the filer" 
                                                fullWidth multiline rows={4} 
                                                // onChange={(e) => setFieldValue("filerNotes", e.target.value)} 
                                                onChange={handleChange}
                                                value={values.filerNotes} 
                                                onBlur={() => setFieldTouched("filerNotes", true)} 
                                            />
                                        </FormControl>
                                    </Grid>
                                    </>)}

                                    {values.numberOfTags === 'multiple' && (<>
                                        <Grid item xs={12}>
                                            {/* button for 'Extract data now' */}
                                            <Box>
                                                <Button variant="contained" type="button"
                                                    disabled={extractionInProgress || extractionTimedOut || extractedValues.length > 0}
                                                    onClick={handleExtractDataNow}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        backgroundColor: '#2099d2',
                                                        '&:hover': { backgroundColor: '#176990' },
                                                        px: 2.5,
                                                    }}
                                                >
                                                    {extractionInProgress ? (
                                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                                            <CircularProgress size={18} color="inherit" />
                                                            Extracting…
                                                        </Box>
                                                    ) : ('Extract data now')}
                                                </Button>
                                                {extractionInProgress && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        Extraction in progress - you may navigate away and will receive a notification when extraction is complete.
                                                    </Typography>
                                                )}
                                                {extractionTimedOut && extractedValues.length === 0 && (
                                                    <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                                                        Extraction is taking longer than expected. You'll receive an alert in My Notifications with the results once it's complete.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>

                                        {/* Extracted values – same UI as single tag, one block per tag */}
                                        <Grid item xs={12}>
                                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                                Extracted values
                                            </Typography>
                                            <FormHelperText className='formtexthelper-heading' sx={{ mb: 1 }}>Review and edit the extracted tag data below.</FormHelperText>
                                            {extractedValues.length === 0 ? (
                                                <Paper variant="outlined" sx={{ py: 4, px: 2, textAlign: 'center', borderStyle: 'dashed', borderColor: 'divider', backgroundColor: 'grey.50' }}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        No extracted values yet. Upload a document and click &quot;Extract data now&quot; to see results.
                                                    </Typography>
                                                </Paper>
                                            ) : (
                                                extractedValues.map((value, index) => (
                                                    <Box key={index} sx={{ mt: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                Tag {index + 1}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <MenuItem dense onClick={() => removeExtractedTag(index)}
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                        py: 0.5,
                                                                        minHeight: 36,
                                                                        color: 'text.secondary',
                                                                        '&:hover': { color: 'error.main', bgcolor: 'action.hover' },
                                                                    }}
                                                                >
                                                                    <CancelIcon sx={{ fontSize: 18, mr: 0.75 }} />
                                                                    Remove tag
                                                                </MenuItem>
                                                            </Box>
                                                        </Box>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <FormControl sx={{ width: '100%' }}>
                                                                    <Grid container spacing={2}>
                                                                        {/* <Grid item xs={12} md={3}>
                                                                            <TextField label="Invoice Number"
                                                                                value={value.invoiceNumber ?? ''}
                                                                                onChange={(e) => updateExtractedValue(index, 'invoiceNumber', e.target.value)}
                                                                                fullWidth
                                                                            />
                                                                        </Grid> */}
                                                                        <Grid item xs={12} md={3}>
                                                                            <TextField label="Insured Name"
                                                                                value={value.insuredName ?? ''}
                                                                                onChange={(e) => updateExtractedValue(index, 'insuredName', e.target.value)}
                                                                                fullWidth
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={3}>
                                                                            <TextField label="Policy Number"
                                                                                value={value.policyNumber ?? ''}
                                                                                onChange={(e) => updateExtractedValue(index, 'policyNumber', e.target.value)}
                                                                                fullWidth
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={3}>
                                                                            <TextField label="Tag Number"
                                                                                value={value.tagNumber ?? ''}
                                                                                onChange={(e) => updateExtractedValue(index, 'tagNumber', e.target.value)}
                                                                                fullWidth
                                                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 100 }}
                                                                                error={(value.tagNumber?.length ?? 0) >= 100}
                                                                                helperText={(value.tagNumber?.length ?? 0) >= 100 ? 'Limited to 100 characters' : ''}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={3}>
                                                                            <TextField label="Due date" type="date"
                                                                                value={value.dueDate ?? ''}
                                                                                onChange={(e) => updateExtractedValue(index, 'dueDate', e.target.value)}
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                                required
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <FormControl sx={{ width: '100%' }} error={(value.errorTagCodes?.length ?? 0) < 1}>
                                                                    <FormHelperText className='formtexthelper-heading'>Error Type Code:</FormHelperText>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                        Select at least one error type for this tag.
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                                                        {(value.errorTagCodes ?? []).map((id) => {
                                                                            const et = errorTagCodes.find((e) => e.errorTagCodeId === id);
                                                                            return et ? (
                                                                                <Chip key={et.errorTagCodeId}
                                                                                    size="small"
                                                                                    label={`${et.errorTagCodeId}: ${et.errorTagName}`}
                                                                                    onDelete={() => {
                                                                                        const next = (value.errorTagCodes ?? []).filter((x) => x !== id);
                                                                                        updateExtractedValue(index, 'errorTagCodes', next);
                                                                                    }}
                                                                                    deleteIcon={<CancelIcon />}
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                />
                                                                            ) : null;
                                                                        })}
                                                                        <Select renderValue={() => 'Add error type…'}
                                                                            size="small"
                                                                            value=""
                                                                            displayEmpty
                                                                            onChange={(e) => {
                                                                                const id = Number(e.target.value);
                                                                                if (id) {
                                                                                    const current = value.errorTagCodes ?? [];
                                                                                    updateExtractedValue(index, 'errorTagCodes', [...current, id]);
                                                                                }
                                                                            }}
                                                                            sx={{ minWidth: 160, height: 32 }}
                                                                        >
                                                                            <MenuItem value="" disabled>Add error type…</MenuItem>
                                                                            {errorTagCodes.filter((et) => !(value.errorTagCodes ?? []).includes(et.errorTagCodeId))
                                                                                .map((et) => (
                                                                                    <MenuItem key={et.errorTagCodeId} value={et.errorTagCodeId}>
                                                                                        {et.errorTagCodeId}: {et.errorTagName}
                                                                                    </MenuItem>
                                                                                ))}
                                                                        </Select>
                                                                    </Box>
                                                                    {(value.errorTagCodes?.length ?? 0) < 1 && (
                                                                        <FormHelperText error sx={{ mt: 0.5 }}>
                                                                            At least one error type code is required for this tag.
                                                                        </FormHelperText>
                                                                    )}
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <FormControl sx={{ width: '100%' }}>
                                                                    {/* <FormHelperText className='formtexthelper-heading'></FormHelperText> */}
                                                                    <TextField label="Filer Notes"
                                                                        fullWidth
                                                                        multiline
                                                                        rows={5}
                                                                        value={value.filerNotes ?? ''}
                                                                        onChange={(e) => updateExtractedValue(index, 'filerNotes', e.target.value)}
                                                                    />
                                                                </FormControl>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                ))
                                            )}
                                        </Grid>
                                    </>)}
                                </Grid>
                            </DialogContent>
                            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    gap: 1.5,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={onCloseModal}
                                    startIcon={<CloseOutlinedIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#475569',
                                        '&:hover': {
                                            borderColor: '#2099d2',
                                            backgroundColor: '#2099d214',
                                            color: '#2099d2',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    type="submit"
                                    disabled={
                                        isSubmitting ||
                                        !isValid ||
                                        values.numberOfTags === '' ||
                                        (values.numberOfTags === 'multiple' && extractedValues.length === 0) ||
                                        (values.numberOfTags === 'multiple' &&
                                            extractedValues.some((tag) => (tag.errorTagCodes?.length ?? 0) < 1)) ||
                                        (values.numberOfTags === 'multiple' &&
                                            extractedValues.some((tag) => !String(tag.dueDate ?? '').trim()))
                                    }
                                    startIcon={<SendOutlinedIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#2099d2',
                                        '&:hover': { backgroundColor: '#176990' },
                                        px: 2.5,
                                    }}
                                >
                                    Send to Client
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};
export default AddReportTag;